import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

const styles = {
    container: {
        display: "flex",
        gap: 20,
        padding: 20,
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#fff",
        color: "#400000",
        minHeight: "85vh",
    },
    panel: {
        flex: 1,
        padding: 20,
        borderRadius: 10,
        backgroundColor: "#fff",
        boxShadow: "0 4px 12px rgba(64, 0, 0, 0.2)",
        border: "1px solid #800000",
        overflowY: "auto",
        maxHeight: "80vh",
    },
    heading: {
        marginBottom: 10,
        color: "#800000",
        borderBottom: "2px solid #800000",
        paddingBottom: 5,
    },
    block: {
        backgroundColor: "#fff4f4",
        padding: 12,
        marginBottom: 10,
        border: "1px solid #800000",
        cursor: "grab",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(128, 0, 0, 0.15)",
        transition: "background 0.2s",
        fontWeight: "600",
        userSelect: "none",
        whiteSpace: "pre-line",
    },
    placeholder: {
        color: "#aaa",
        fontStyle: "italic",
        marginTop: 10,
    },
    outputContainer: {
        whiteSpace: "pre-line",
        lineHeight: 1.7,
        fontSize: 16,
        color: "#400000",
    },
    copyButton: {
        marginTop: 20,
        padding: "10px 15px",
        backgroundColor: "#800000",
        color: "white",
        border: "none",
        borderRadius: 5,
        cursor: "pointer",
    },
    moveButtons: {
        marginTop: 5,
        display: "flex",
        gap: 8,
    },
    moveBtn: {
        padding: "4px 8px",
        fontSize: 12,
        backgroundColor: "#f0f0f0",
        border: "1px solid #ccc",
        borderRadius: 4,
        cursor: "pointer",
    },
    sectionLabel: {
        fontWeight: "700",
        marginTop: 20,
        marginBottom: 8,
        color: "#800000",
        borderBottom: "1px solid #800000",
        paddingBottom: 3,
    },
    emailLine: {
        marginBottom: "14px",
        padding: "10px",
        backgroundColor: "#fff7f7",
        border: "1px dashed #800000",
        borderRadius: "6px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: "14px",
    },
    lineControls: {
        display: "flex",
        gap: "6px",
    },
    arrowBtn: {
        background: "none",
        border: "1px solid #aaa",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        padding: "2px 6px",
    },
    deleteBtn: {
        background: "#ffeded",
        border: "1px solid #d00",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "14px",
        color: "#a00000",
        padding: "2px 6px",
    },
};

const greetingOptions = ["Dear [First Name],", "Dear Mr. [Last Name],"];

const closingSignatureBlocks = {
    formal: `Yours sincerely,\n[Your Name]`,
    semiFormal: `Yours sincerely,\n[Your Name]`,
    informal: `Yours truly,\n[Your Name]`,
};

const paragraphsByToneAndSituation = {
    formal: {
        complaint: [
            greetingOptions[1],
            "I am writing to formally express my dissatisfaction regarding the recent service I received at your establishment.",
            "Unfortunately, the experience was unsatisfactory due to repeated delays and lack of proper communication.",
            "Despite several attempts to seek resolution, the issue remains unresolved and has caused significant inconvenience.",
            "I kindly request that this matter be addressed promptly to prevent future occurrences.",
            "Thank you for your attention to this serious concern.",
        ],
        request: [
            greetingOptions[1],
            "I am writing to request your assistance regarding a matter of importance.",
            "I would greatly appreciate if you could provide the necessary information at your earliest convenience.",
            "Your support in this regard would be highly valuable and appreciated.",
            "Please let me know if any further details are required.",
            "Thank you very much for your time and consideration.",
        ],
        thankYou: [
            greetingOptions[1],
            "I am writing to sincerely thank you for your excellent support.",
            "Your assistance was invaluable and greatly appreciated.",
            "I am truly grateful for the prompt and professional service provided.",
            "Please accept my heartfelt thanks.",
            "I look forward to continuing our successful relationship.",
        ],
        meeting: [
            greetingOptions[1],
            "I am writing to schedule a meeting to discuss important matters.",
            "Please let me know your availability so we can arrange a suitable time.",
            "Your input on the topics will be highly valuable.",
            "I look forward to your confirmation.",
            "Thank you for your attention.",
        ],
    },
    semiFormal: {
        meetingTeam: [
            greetingOptions[0],
            "I would like to arrange a meeting with the team to discuss our ongoing projects and upcoming deadlines.",
            "Please let me know your availability so we can schedule a convenient time for everyone.",
            "Your participation and input will be highly valuable.",
            "Looking forward to your response.",
        ],
        meetingNeighbour: [
            greetingOptions[0],
            "I am writing to request a meeting with you to discuss some neighborhood matters.",
            "Please let me know a suitable time when we can meet and talk.",
            "I believe a face-to-face discussion will help resolve any concerns effectively.",
            "Thank you for your cooperation.",
        ],
        noiseComplaint: [
            greetingOptions[0],
            "I would like to bring to your attention some noise disturbances recently affecting my household.",
            "The noise levels have been quite disruptive, especially during late hours.",
            "I kindly ask if you could reduce the noise to help maintain a peaceful environment.",
            "Thank you for your understanding and cooperation.",
        ],
        clientComplaint: [
            greetingOptions[0],
            "I am writing to express some concerns regarding the service I have received recently.",
            "There have been several issues that impacted my experience negatively.",
            "I hope these matters can be addressed promptly to restore my confidence in your service.",
            "Thank you for your attention to this issue.",
        ],
    },
    informal: {
        meetFriend: [
            greetingOptions[0],
            "I hope you’re doing well! It’s been a while since we caught up.",
            "I was wondering if you’d like to meet sometime soon and hang out.",
            "Let me know when you’re free, and we can plan something fun.",
            "Looking forward to seeing you!",
        ],
        thankSupport: [
            greetingOptions[0],
            "Just wanted to say a big thank you for all the support you’ve given me lately.",
            "It really means a lot, and I couldn’t have managed without you.",
            "Thanks again for always being there.",
            "Take care!",
        ],
        tellLife: [
            greetingOptions[0],
            "I wanted to share some updates about what’s been going on in my life recently.",
            "Things have been pretty exciting, and I can’t wait to tell you all about it.",
            "Hope we can catch up soon so I can fill you in properly!",
            "Take care until then.",
        ],
        casualGreeting: [
            greetingOptions[0],
            "Hey! Just thought I’d drop you a quick note to say hi.",
            "Hope everything’s great with you.",
            "Let’s chat soon!",
        ],
    },
};

const transitionWords = [
    "Furthermore,",
    "Moreover,",
    "In addition,",
    "Consequently,",
    "Therefore,",
    "However,",
    "On the other hand,",
    "As a result,",
    "Nevertheless,",
    "In contrast,",
    "Firstly,",
    "Secondly,",
    "Lastly,",
    "To summarize,",
    "In conclusion,",
    "For example,",
    "Such as,",
    "In particular,",
    "Hence,",
    "Meanwhile,",
];

const adjectives = [
    "significant",
    "substantial",
    "noteworthy",
    "critical",
    "reliable",
    "effective",
    "prompt",
    "professional",
    "valuable",
    "considerable",
];

const extraSentences = [
    "I would appreciate your prompt attention to this matter.",
    "Please do not hesitate to contact me should you require any further information.",
    "Your understanding and cooperation are greatly appreciated.",
    "I look forward to your timely response.",
    "Thank you for considering my request.",
    "I hope this issue can be resolved soon.",
    "Please let me know if you need any clarification.",
    "I trust you will handle this with priority.",
    "Thank you for your continued support.",
    "I am confident that we can find a mutually beneficial solution.",
];

const situationOptionsByTone = {
    formal: [
        { value: "complaint", label: "Complaint" },
        { value: "request", label: "Request" },
        { value: "thankYou", label: "Thank You" },
        { value: "meeting", label: "Meeting" },
    ],
    semiFormal: [
        { value: "meetingTeam", label: "Meeting with Team" },
        { value: "meetingNeighbour", label: "Meeting with Neighbour" },
        { value: "noiseComplaint", label: "Noise Complaint" },
        { value: "clientComplaint", label: "Client Complaint" },
    ],
    informal: [
        { value: "meetFriend", label: "Meet a Friend" },
        { value: "thankSupport", label: "Thank for Support" },
        { value: "tellLife", label: "Tell About Your Life" },
        { value: "casualGreeting", label: "Casual Greeting" },
    ],
};

export default function WritingTemplateBuilder() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth?.authData);
    const hasRun = useRef(false);

    const getStoredUser = () => {
        if (reduxUser?.result) return reduxUser.result;
        const localUser = JSON.parse(localStorage.getItem("user_info"));
        if (localUser?.result) return localUser.result;
        return null;
    };

    const [, setCurrentUser] = useState(getStoredUser);
    const [hasAccess, setHasAccess] = useState(false);
    const [tone, setTone] = useState("formal");
    const [situation, setSituation] = useState(situationOptionsByTone.formal[0].value);
    const [emailLines, setEmailLines] = useState([]);

    // Authentication and access check with ref guard
    useEffect(() => {
        if (hasRun.current) return; // Prevent duplicate execution
        hasRun.current = true;

        const storedUser = getStoredUser();
        setCurrentUser(storedUser);

        if (!storedUser) {
            toast.error("Please login to access templates");
            navigate("/login");
            return;
        }

        const userPackage = storedUser.packageName || localStorage.getItem("userPackage");
        const allowedPackages = ["30-DAYS-PACKAGE", "60-DAYS-PACKAGE", "90-DAYS-PACKAGE"];

        if (!allowedPackages.includes(userPackage)) {
            toast.info("Templates require a premium package subscription");
            navigate("/pricing");
            return;
        }

        if (!reduxUser && storedUser) {
            dispatch({ type: "AUTH", data: storedUser });
        }

        setHasAccess(true);
    }, [reduxUser, dispatch, navigate]);

    // Reset email lines when tone changes
    useEffect(() => {
        setEmailLines([]);
        setSituation(situationOptionsByTone[tone][0].value);
    }, [tone]);

    if (!hasAccess) {
        return null;
    }

    const mainParagraphs = paragraphsByToneAndSituation[tone][situation] || [];

    const handleDragStart = (e, text) => {
        e.dataTransfer.setData("text/plain", text);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const text = e.dataTransfer.getData("text/plain");
        setEmailLines((prev) => [...prev, text]);
    };

    const allowDrop = (e) => e.preventDefault();

    const handleCopy = () => {
        if (emailLines.length === 0) {
            alert("Please drag some paragraphs first!");
            return;
        }
        const emailText = emailLines.join("\n\n");
        navigator.clipboard.writeText(emailText);
        alert("Email copied to clipboard!");
    };

    const moveLine = (index, direction) => {
        const newLines = [...emailLines];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newLines.length) return;
        [newLines[index], newLines[targetIndex]] = [
            newLines[targetIndex],
            newLines[index],
        ];
        setEmailLines(newLines);
    };

    return (
        <>
            <h1 style={{ textAlign: "center", color: "#800000" }}>
                CELPIP Writing Template Builder
            </h1>

            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 20,
                    marginBottom: 20,
                }}
            >
                <label
                    htmlFor="tone"
                    style={{ fontWeight: "700", color: "#800000", alignSelf: "center" }}
                >
                    Select Tone:
                </label>
                <select
                    id="tone"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    style={{
                        padding: 8,
                        borderRadius: 5,
                        border: "1px solid #800000",
                        fontWeight: "600",
                    }}
                >
                    <option value="formal">Formal</option>
                    <option value="semiFormal">Semi-formal</option>
                    <option value="informal">Informal</option>
                </select>

                <label
                    htmlFor="situation"
                    style={{ fontWeight: "700", color: "#800000", alignSelf: "center" }}
                >
                    Select Situation:
                </label>
                <select
                    id="situation"
                    value={situation}
                    onChange={(e) => setSituation(e.target.value)}
                    style={{
                        padding: 8,
                        borderRadius: 5,
                        border: "1px solid #800000",
                        fontWeight: "600",
                    }}
                >
                    {situationOptionsByTone[tone].map(({ value, label }) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            </div>

            <div style={styles.container}>
                {/* Left panel */}
                <div style={styles.panel}>
                    <h2 style={styles.heading}>Paragraph Blocks</h2>

                    <div style={styles.sectionLabel}>Main Paragraphs</div>
                    {mainParagraphs.map((para, idx) => (
                        <div
                            key={"main-" + idx}
                            draggable
                            onDragStart={(e) => handleDragStart(e, para)}
                            style={styles.block}
                            title="Drag to add this paragraph"
                        >
                            {para}
                        </div>
                    ))}

                    <div style={styles.sectionLabel}>Transition Words</div>
                    {transitionWords.map((tw, idx) => (
                        <div
                            key={"tw-" + idx}
                            draggable
                            onDragStart={(e) => handleDragStart(e, tw)}
                            style={styles.block}
                        >
                            {tw}
                        </div>
                    ))}

                    <div style={styles.sectionLabel}>Vocabulary Adjectives</div>
                    {adjectives.map((adj, idx) => (
                        <div
                            key={"adj-" + idx}
                            draggable
                            onDragStart={(e) => handleDragStart(e, adj)}
                            style={styles.block}
                        >
                            {adj}
                        </div>
                    ))}

                    <div style={styles.sectionLabel}>Extra Sentences</div>
                    {extraSentences.map((sent, idx) => (
                        <div
                            key={"extra-" + idx}
                            draggable
                            onDragStart={(e) => handleDragStart(e, sent)}
                            style={styles.block}
                        >
                            {sent}
                        </div>
                    ))}

                    <div style={styles.sectionLabel}>Closing & Signature</div>
                    <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, closingSignatureBlocks[tone])}
                        style={styles.block}
                    >
                        {closingSignatureBlocks[tone]}
                    </div>
                </div>

                {/* Right panel */}
                <div style={styles.panel} onDrop={handleDrop} onDragOver={allowDrop}>
                    <h2 style={styles.heading}>Your Email Template</h2>
                    {emailLines.length === 0 ? (
                        <p style={styles.placeholder}>
                            Drag and drop paragraphs here to build your letter.
                        </p>
                    ) : (
                        <div style={styles.outputContainer}>
                            {emailLines.map((line, idx) => (
                                <div key={idx} style={styles.emailLine}>
                                    <span>{line}</span>
                                    <div style={styles.lineControls}>
                                        <button
                                            onClick={() => moveLine(idx, -1)}
                                            style={styles.arrowBtn}
                                            disabled={idx === 0}
                                            title="Move up"
                                        >
                                            ⬆️
                                        </button>
                                        <button
                                            onClick={() => moveLine(idx, 1)}
                                            style={styles.arrowBtn}
                                            disabled={idx === emailLines.length - 1}
                                            title="Move down"
                                        >
                                            ⬇️
                                        </button>
                                        <button
                                            onClick={() => {
                                                const updated = [...emailLines];
                                                updated.splice(idx, 1);
                                                setEmailLines(updated);
                                            }}
                                            style={styles.deleteBtn}
                                            title="Delete line"
                                        >
                                            ✖
                                        </button>
                                        <button
                                            onClick={() => {
                                                const updated = [...emailLines];
                                                updated.splice(idx, 1);
                                                setEmailLines(updated);
                                            }}
                                            style={{
                                                ...styles.deleteBtn,
                                                backgroundColor: "#fff",
                                                border: "1px solid #800000",
                                                color: "#800000",
                                                fontWeight: "600",
                                                padding: "2px 6px",
                                                cursor: "pointer",
                                            }}
                                            title="Clear line"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button style={styles.copyButton} onClick={handleCopy}>
                        Copy Letter
                    </button>
                    <button
                        style={{
                            ...styles.copyButton,
                            marginLeft: 10,
                            backgroundColor: "#aa0000",
                        }}
                        onClick={() => setEmailLines([])}
                        disabled={emailLines.length === 0}
                        title="Clear all lines"
                    >
                        Clear All
                    </button>
                </div>
            </div>
        </>
    );
}
